import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';

import NavigationBar from '../components/NavigationBar';
import { BRANDING } from '../config/branding';

const ContactPage = () => {
  const addressText = BRANDING.pdf.addressLines.join(', ');
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    addressText || BRANDING.companyName
  )}`;
  const whatsappUrl = `https://wa.me/${BRANDING.whatsappNumber}?text=${encodeURIComponent(
    `Hola, me gustaría recibir más información sobre ${BRANDING.companyName}.`
  )}`;

  return (
    <>
      <NavigationBar />
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1b033d 0%, #44108f 50%, #7821d6 100%)',
          color: 'white',
          pb: 10,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'radial-gradient(circle at 10% 20%, rgba(255,255,255,0.12), transparent 40%), radial-gradient(circle at 80% 0%, rgba(0,255,255,0.15), transparent 25%)',
            opacity: 0.8,
          }}
        />

        <Container maxWidth="lg" sx={{ pt: 10, position: 'relative', zIndex: 1 }}>
          <Stack spacing={2} textAlign="center" mb={6}>
            <Typography variant="overline" sx={{ letterSpacing: 4 }}>
              Contacto
            </Typography>
            <Typography variant="h3" fontWeight={800}>
              Hablemos
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Elige el canal que prefieras. La información cambia automáticamente según el perfil de empresa.
            </Typography>
            <Stack direction="row" justifyContent="center" gap={1} flexWrap="wrap">
              <Chip
                label={BRANDING.companyName}
                sx={{
                  bgcolor: 'rgba(0,212,255,0.18)',
                  color: 'white',
                  fontWeight: 700,
                }}
              />
              {BRANDING.companyTagline ? (
                <Chip
                  label={BRANDING.companyTagline}
                  sx={{
                    bgcolor: 'rgba(124,58,237,0.18)',
                    color: 'rgba(255,255,255,0.92)',
                    fontWeight: 600,
                  }}
                />
              ) : null}
            </Stack>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  bgcolor: 'rgba(13,20,60,0.82)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  boxShadow: '0 20px 50px rgba(5,5,20,0.6)',
                }}
              >
                <CardContent>
                  <Typography variant="h5" fontWeight={800} gutterBottom>
                    Canales directos
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mb: 3 }}>
                    Respuesta rápida para cotizaciones, disponibilidad y pedidos.
                  </Typography>
                  {BRANDING.hoursText ? (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.82)', mb: 2 }}>
                      Horario: <strong>{BRANDING.hoursText}</strong>
                    </Typography>
                  ) : null}

                  <Stack spacing={1.2}>
                    <Button
                      variant="contained"
                      startIcon={<WhatsAppIcon />}
                      component="a"
                      href={whatsappUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        borderRadius: 999,
                        py: 1.2,
                        textTransform: 'none',
                        fontWeight: 800,
                        backgroundImage: 'linear-gradient(90deg, #22c55e, #16a34a)',
                      }}
                    >
                      Escribir por WhatsApp
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<PhoneIcon />}
                      component="a"
                      href={`tel:${BRANDING.telHref}`}
                      sx={{
                        borderRadius: 999,
                        py: 1.2,
                        textTransform: 'none',
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.92)',
                        borderColor: 'rgba(255,255,255,0.28)',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.55)' },
                      }}
                    >
                      Llamar ({BRANDING.phoneText})
                    </Button>

                    <Button
                      color="secondary"
                      startIcon={<MailOutlineIcon />}
                      component="a"
                      href={`mailto:${BRANDING.companyEmail}`}
                      sx={{
                        borderRadius: 999,
                        py: 1.2,
                        textTransform: 'none',
                        fontWeight: 700,
                        bgcolor: 'rgba(255,255,255,0.08)',
                        color: 'rgba(255,255,255,0.92)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                      }}
                    >
                      {BRANDING.companyEmail}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  bgcolor: 'rgba(13,20,60,0.82)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  boxShadow: '0 20px 50px rgba(5,5,20,0.6)',
                }}
              >
                <CardContent>
                  <Typography variant="h5" fontWeight={800} gutterBottom>
                    Dirección
                  </Typography>
                  <Stack spacing={1.3} sx={{ color: 'rgba(255,255,255,0.82)' }}>
                    {BRANDING.pdf.addressLines.map((line) => (
                      <Stack key={line} direction="row" spacing={1} alignItems="flex-start">
                        <RoomOutlinedIcon sx={{ mt: 0.2, opacity: 0.9 }} />
                        <Typography variant="body2">{line}</Typography>
                      </Stack>
                    ))}
                    <Button
                      variant="outlined"
                      component="a"
                      href={mapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        mt: 1,
                        borderRadius: 999,
                        py: 1.1,
                        textTransform: 'none',
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.92)',
                        borderColor: 'rgba(255,255,255,0.28)',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.55)' },
                      }}
                    >
                      Ver en Google Maps
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default ContactPage;
